import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { Link } from 'react-router-dom';

import {
  Button,
  Typography,
} from '../base';

import {
  rootUrl,
} from '../../constants/urls';

import { timeFormat } from '../../constants/time';

import {
  headerHeight,
  subHeaderHeight,
} from '../../constants/styles';

import { removeLeadingZeros } from '../../utils/utils';

import Clear from '@material-ui/icons/Clear';

const styles = theme => ({
    header: {
      position: 'fixed',
      top: headerHeight,
      width: '100%',
      height: subHeaderHeight,
      background: theme.palette.background.paper,
      borderTop: `1px solid ${theme.palette.grey[400]}`,
      padding: theme.spacing(),
      display: 'flex',
      justifyContent: 'space-between',
      boxShadow: theme.shadows[5],
      zIndex: 1001,

      '@media (min-width: 359px)': {
        padding: theme.spacing(2),
      },

      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
      },
    },
    info: {
      display: 'flex',
      alignItems: 'center',
    },
    cancelButton: {
      padding: 0,
      minWidth: 30,
      borderRadius: '50%',
      color: theme.palette.text.primary,

      '&:hover': {
        color: theme.palette.text.primary,
      },
    },
    titleContainer: {
      flex: 1,
      paddingLeft: theme.spacing(),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',

      [theme.breakpoints.up('sm')]: {
        borderLeft: '1px solid grey',
        paddingLeft: theme.spacing(3),
        marginLeft: theme.spacing(),
      }
    },
    title: {
      fontSize: 16,

      [theme.breakpoints.up('sm')]: {
        fontSize: 20,
      },

      [theme.breakpoints.up('lg')]: {
        fontSize: 22
      },
    },
    titleSmall: {
      fontSize: 14,

      [theme.breakpoints.up('sm')]: {
        fontSize: 20,
      },

      [theme.breakpoints.up('lg')]: {
        fontSize: 22
      },
    },
    subtitleSmall: {
      fontSize: 12,
    },
    buttons: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    actionButton: {
      color: theme.palette.primary.main,
      padding: `${theme.spacing(0.5)}px ${theme.spacing()}px`,
      alignSelf: 'center',
      fontSize: 13,

      [theme.breakpoints.up('sm')]: {
        padding: `${theme.spacing(2)}px ${theme.spacing(4)}px`,
        fontSize: 16,
      },
    },
    downloadButton: {
      color: theme.palette.primary.main,
      padding: `${theme.spacing(0.5)}px ${theme.spacing()}px`,
      alignSelf: 'center',
      fontSize: 13,
      marginRight: `${theme.spacing(1)}px`,

      [theme.breakpoints.up('sm')]: {
        padding: `${theme.spacing(2)}px ${theme.spacing(4)}px`,
        fontSize: 16,
      },
    },
})

const SubHeader = props => {
  const {
    translations: t,
    styles: s,
    classes: c,
    view,
    job: {
      id,
      notificationNumber,
      serviceOrderNumber,
      manual,
    },
    actionButton: {
      disabled,
      type,
      onActionClick,
      condition,
    },
    downloadButton: {
      onActionClickDownload,
      conditionDownload,
      isDisabled,
    },
    cancelButton: {
      route,
    },
  } = props;
  const today = moment().format(timeFormat.DATE);
  const isSingleJob = view === 'singleJob';

  return (
    <div className={c.header} styles={s.header}>
      <div className={c.info}>
        <Link className={c.cancelButton} to={route}>
          <Clear />
        </Link>
        <div className={c.titleContainer} styles={s.title}>
          <Typography className={isSingleJob ? c.titleSmall : c.title} variant='h6'>{t.title} {id ? `#${id}` : null}</Typography>
          { isSingleJob ?
            !manual
              ? <Fragment>
                  <Typography className={isSingleJob ? c.subtitleSmall : ''} variant='caption'>
                  {`${t.notificationNumber}: ${notificationNumber ? removeLeadingZeros(notificationNumber) : t.noInfo}`}
                  </Typography>
                  <Typography className={isSingleJob ? c.subtitleSmall : ''} variant='caption'>
                  {`${t.serviceOrderNumber}: ${serviceOrderNumber ? removeLeadingZeros(serviceOrderNumber) : t.noInfo}`}
                  </Typography>
                </Fragment>
              : null
            : <Typography className={isSingleJob ? c.subtitleSmall : ''} variant='caption'>
                {`${t.createdAt}: ${today}`}
              </Typography>
          }
        </div>
      </div>
      { condition &&
        <div className={c.buttons}>
          {conditionDownload && 
            <Button
              disabled={isDisabled}
              variant='contained'
              type={type}
              color='secondary'
              className={c.downloadButton}
              styles={s.actionButton}
              onClick={onActionClickDownload}
            >
              {t.downloadButton}
            </Button>
          }

          <Button
            variant='contained'
            type={type}
            color='secondary'
            className={c.actionButton}
            styles={s.actionButton}
            disabled={disabled}
            onClick={onActionClick}
          >
            {t.actionButton}
          </Button>
        </div>
      }
    </div>
  );
}

SubHeader.defaultProps = {
  view: '',
  job: {
    id: '',
    createdAt: '',
    modifiedAt: '',
  },
  styles: {
    header: {},
    cancelButton: {},
    title: {},
    actionButton: {},
  },
  actionButton: {
    disabled: false,
    type: 'submit',
    onActionClick: () => {},
  },
  downloadButton: {
    onActionClickDownload: () => {},
  },
  cancelButton: {
    route: rootUrl(),
  }
}

SubHeader.propTypes = {
  view: PropTypes.oneOf(['addJob', 'importJob', 'singleJob']),
  job: PropTypes.shape({
    id: PropTypes.string,
    createdAt: PropTypes.string,
    modifiedAt: PropTypes.string,
  }),
  translations: PropTypes.shape({
    title: PropTypes.string.isRequired,
    actionButton: PropTypes.string.isRequired,
  }),
  styles: PropTypes.shape({
    header: PropTypes.object,
    cancelButton: PropTypes.object,
    title: PropTypes.object,
    actionButton: PropTypes.object,
    downloadButton: PropTypes.object,
  }),
  actionButton: PropTypes.shape({
    disabled: PropTypes.bool,
    type: PropTypes.string,
    onClick: PropTypes.func,
  }),
  downloadButton: PropTypes.shape({
    type: PropTypes.string,
    onClick: PropTypes.func,
  }),
  cancelButton: PropTypes.shape({
    route: PropTypes.string,
  }),
};

export default withStyles(styles)(SubHeader);



// WEBPACK FOOTER //
// ./src/components/SubHeader/SubHeader.js